/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import IEnergyManagementProps, {
  EUtilityConfig,
  ECartAt,
  IAny
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import UtilityBillPerformanceDetailComponent from '../UtilityBillPerformanceDetailWidget/UtilityBillPerformanceDetailWidget';
import SavingsByMeterComponent from '../SavingsByMeterWidget/SavingsByMeterComponent';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import useMounted from '../../shared/useMount/useMount';
import {
  excludeCurrentMonth,
  formattedDate,
  getEndDateForUtility,
  getFirstDayofSelectedMonth
} from '../../shared/Utils/Utils';
import { UtilityType } from '../../shared/Utils/Constants';
import { getConnctorConfig } from '../../shared/Utils/DataConnectorUtils';
import { ApiContext } from '../../../context/ApiContext';
import UtilitySummaryWidget from './UtilitySummary/UtilitySummaryWidget';
import UtilityBillBreakdownChartWidget from './BillBreakdownWidgets/UtilityBillBreakdownChartWidget';
import { Button, Icon, Tooltip, Notification, Card } from '@scuf/common';
import './UtilitySavingDetailsHOC.scss';
import { getUtilityTitle } from './UtilityBillViewDetailsUtils';
import moment from 'moment';
import CommonUtilitySummaryTable from './UtilitySummary/CommonUtilitySummaryTable';
import { EmaasSubscriptionType } from '../../shared/Enums';

const portfolioUtilityConfig = [
  EUtilityConfig.electricityUtilityPortfolioLabel,
  EUtilityConfig.gasUtilityPortfolioLabel,
  EUtilityConfig.waterUtilityPortfolioLabel,
  EUtilityConfig.seweragePortfolioLabel
];

export const siteLabelUtilityConfig = [
  EUtilityConfig.electricityUtilitySiteLabel,
  EUtilityConfig.gasUtilitySiteLabel,
  EUtilityConfig.waterUtilitySiteLabel,
  EUtilityConfig.sewerageUtilitySiteLabel
];

const selectedUtility = [
  UtilityType.Electricity,
  UtilityType.Gas,
  UtilityType.Water,
  UtilityType.Sewerage
];
interface IButtongroup {
  content: string;
  type:
    | 'link'
    | 'primary'
    | 'secondary'
    | 'inline'
    | 'inline-secondary'
    | undefined;
}
export interface IEnergyManagementHOCProps extends IEnergyManagementProps {
  ActiveIndex: number;
  utilityType: string;
}

const UtilitySavingDetailsHOC = (props: IEnergyManagementHOCProps) => {
  const [t] = useTranslation();
  const apiContext = useContext(ApiContext);
  const d = descriptors.energyManagement;
  const {
    bearerToken,
    requestContext,
    ActiveIndex,
    utilityType,
    emaasContext
  } = props;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);

  const [buttonGroup, setButtonGroup] = useState<IButtongroup[]>(
    apiContext.basicAdvancedGrp
      ? apiContext.basicAdvancedGrp
      : [
          { content: t(d.BASIC), type: 'primary' },
          { content: t(d.ADVANCED), type: 'secondary' }
        ]
  );

  const [buttonGroupBilling, setButtonGroupBilling] = useState<IButtongroup[]>(
    apiContext.monthlyBillingGrp
      ? apiContext.monthlyBillingGrp
      : [
          { content: 'Monthly', type: 'primary' },
          { content: 'Billing', type: 'secondary' }
        ]
  );
  const activeTab = ActiveIndex - 1;
  const UtilityBillType = selectedUtility[activeTab];

  const [isOverview, setIsOverview] = useState(apiContext.overViewElectricity);
  const [isMonthly, setIsMonthly] = useState<IAny>(
    apiContext.isMonthlyElectricity
  );

  const onBackarrowClick = () => setIsOverview(true);
  const onViewDetailsClick = () => {
    apiContext.overviewSet(false);
    setIsOverview(false);
  };

  const [prevRContext, setPrevRContext] = useState<IAny>(requestContext);
  const [prevRContextAdvnc, setPrevRContextAdvnc] = useState<IAny>(
    requestContext
  );

  const hasRContextChanged = (request: IAny) => {
    return (
      request.context.startTime !== prevRContext.context.startTime ||
      request.context.endTime !== prevRContext.context.endTime ||
      requestContext.context.nodeId !== prevRContext.context.nodeId
    );
  };

  const hasRContextChangedForAdvnc = (request: IAny) => {
    return (
      request.context.startTime !== prevRContextAdvnc.context.startTime ||
      request.context.endTime !== prevRContextAdvnc.context.endTime ||
      requestContext.context.nodeId !== prevRContextAdvnc.context.nodeId ||
      requestContext.context.isCashFlow !== prevRContextAdvnc.context.isCashFlow
    );
  };

  const toggleButtonForMonthlyBilling = (
    clickedButtonIndex: number,
    buttonData: IButtongroup
  ) => {
    const updatedButtons: IButtongroup[] = buttonGroupBilling.map(
      (button: IButtongroup, index: number) => {
        if (clickedButtonIndex === index) {
          return { ...button, type: 'primary' };
        } else {
          return { ...button, type: 'secondary' };
        }
      }
    );

    if (clickedButtonIndex === 0) {
      setIsMonthly(true);
      apiContext.setIsMonthlyElectricity(true);
    } else {
      setIsMonthly(false);
      apiContext.setIsMonthlyElectricity(false);
    }

    apiContext &&
      apiContext.setMonthlyBillingGrp &&
      apiContext.setMonthlyBillingGrp(updatedButtons);

    setButtonGroupBilling(updatedButtons);
  };

  useEffect(() => {
    if (isOverview) {
      setButtonGroupBilling([
        { content: 'Monthly', type: 'primary' },
        { content: 'Billing', type: 'secondary' }
      ]);
      apiContext.setIsMonthlyElectricity(true);
      setIsMonthly(true);
    }
  }, [isOverview]);

  useEffect(() => {
    apiContext.onViewChange && apiContext.onViewChange(isMonthly);
  }, [isMonthly]);

  const toggleButton = (
    clickedButtonIndex: number,
    buttonData: IButtongroup
  ) => {
    const updatedButtons: IButtongroup[] = buttonGroup.map(
      (button: IButtongroup, index: number) => {
        if (clickedButtonIndex === index) {
          setIsOverview(false);
          apiContext.overviewSet(false);
          return { ...button, type: 'primary' };
        } else {
          setIsOverview(true);
          apiContext && apiContext.overviewSet && apiContext.overviewSet(true);
          return { ...button, type: 'secondary' };
        }
      }
    );
    apiContext &&
      apiContext.setBasicAdvancedGrp &&
      apiContext.setBasicAdvancedGrp(updatedButtons);
    setButtonGroup(updatedButtons);
  };

  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );

  const startTimeForBreakdown = moment(
    requestContext.context.startTime!
  ).format('YYYY-MM-DD');
  const endTime =
    requestContext.context.subscriptionType ===
    EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
      ? getEndDateForUtility(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        )
      : excludeCurrentMonth(
          requestContext.context.endTime!,
          requestContext.context.startTime!
        );
  const endTimeNewFormat = formattedDate(endTime);

  const endTimeForBreakdown = moment(requestContext.context.endTime!).format(
    'YYYY-MM-DD'
  );

  const configName =
    apiContext.apiCallAt === ECartAt.portfolioLabel
      ? portfolioUtilityConfig[activeTab]
      : siteLabelUtilityConfig[activeTab];
  const connectorConfig = getConnctorConfig(
    configName,
    requestContext,
    startTime,
    endTimeNewFormat
  );

  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  const isDateRangeValid = () => {
    const sDate = moment(
      getFirstDayofSelectedMonth(requestContext.context.startTime!),
      'YYYY-MM-DD'
    );
    const eDate =
      requestContext.context.subscriptionType ===
      EmaasSubscriptionType.PREMIUM_SUBSCRIPTION
        ? moment(
            getEndDateForUtility(
              requestContext.context.endTime!,
              requestContext.context.startTime!
            ),
            'YYYY-MM-DD'
          )
        : moment(
            excludeCurrentMonth(
              requestContext.context.endTime!,
              requestContext.context.startTime!
            ),
            'YYYY-MM-DD'
          );
    return eDate.diff(sDate, 'days') <= 365;
  };

  useEffect(() => {
    if (isMounted && hasRContextChanged(requestContext)) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);

        setPrevRContext(requestContext);
        dataConnector &&
          dataConnector.actions &&
          dataConnector.actions.getAll();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext]);

  useEffect(() => {
    !isDateRangeValid() && setIsOutOfRange(true);
  }, []);

  const connectorConfigBillBreakdown = {
    name: 'utility-bill-breakdown',
    options: {
      startDate: startTimeForBreakdown,
      endDate: endTimeForBreakdown,
      utilityType: utilityType,
      isCashFlowView: requestContext.context.isCashFlow
        ? requestContext.context.isCashFlow
        : ''
    }
  };

  const connectorConfigBillBreakdownTable = {
    name: 'utility-bill-breakdown',
    options: {
      startDate: startTimeForBreakdown,
      endDate: endTimeForBreakdown,
      utilityType: utilityType,
      isCashFlowView: 'true'
    }
  };

  // const connectorConfigBillBreakdownTableForGas = {
  //   name: 'utility-bill-breakdown-for-Gas',
  //   options: {
  //     startDate: startTime,
  //     endDate: endTimeNewFormat,
  //     utilityType: utilityType,
  //     isCashFlowView: 'true'
  //   }
  // };

  // const connectorConfigBillBreakdownTableForWater = {
  //   name: 'utility-bill-breakdown-for-Water',
  //   options: {
  //     startDate: startTime,
  //     endDate: endTimeNewFormat,
  //     utilityType: utilityType,
  //     isCashFlowView: 'true'
  //   }
  // };

  // const connectorConfigBillBreakdownTableForSewer = {
  //   name: 'utility-bill-breakdown-for-Sewer',
  //   options: {
  //     startDate: startTime,
  //     endDate: endTimeNewFormat,
  //     utilityType: utilityType,
  //     isCashFlowView: 'true'
  //   }
  // };

  const [dataConnectorBillBreakdown] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigBillBreakdown,
      requestContext,
      bearerToken
    },
    !(!props.emaasContext?.forPortfolio && utilityType !== UtilityType.Electric)
  );

  const [dataConnectorBillBreakdownTable] = useConnectorAndDynamicProps(
    {
      connectorConfig: connectorConfigBillBreakdownTable,
      requestContext,
      bearerToken
    },
    !props.emaasContext?.forPortfolio && utilityType !== UtilityType.Electric
  );

  // const [dataConnectorBillBreakdownTableForGas] = useConnectorAndDynamicProps({
  //   connectorConfig: connectorConfigBillBreakdownTableForGas,
  //   requestContext,
  //   bearerToken
  // });

  // const [dataConnectorBillBreakdownTableForWater] = useConnectorAndDynamicProps(
  //   {
  //     connectorConfig: connectorConfigBillBreakdownTableForWater,
  //     requestContext,
  //     bearerToken
  //   }
  // );

  // const [dataConnectorBillBreakdownTableForSewer] = useConnectorAndDynamicProps(
  //   {
  //     connectorConfig: connectorConfigBillBreakdownTableForSewer,
  //     requestContext,
  //     bearerToken
  //   }
  // );

  useEffect(() => {
    if (isMounted && hasRContextChangedForAdvnc(requestContext)) {
      // switch (utilityType) {
      //   case UtilityType.Gas:
      //     dataConnectorBillBreakdownTableForGas &&
      //       dataConnectorBillBreakdownTableForGas.actions &&
      //       dataConnectorBillBreakdownTableForGas.actions.getAll();
      //     break;
      //   case UtilityType.Water:
      //     dataConnectorBillBreakdownTableForWater &&
      //       dataConnectorBillBreakdownTableForWater.actions &&
      //       dataConnectorBillBreakdownTableForWater.actions.getAll();
      //     break;
      //   case UtilityType.Sewerage:
      //     dataConnectorBillBreakdownTableForSewer &&
      //       dataConnectorBillBreakdownTableForSewer.actions &&
      //       dataConnectorBillBreakdownTableForSewer.actions.getAll();
      //     break;
      // default:
      dataConnectorBillBreakdown &&
        dataConnectorBillBreakdown.actions &&
        dataConnectorBillBreakdown.actions.getAll();
      if (
        !props.emaasContext?.forPortfolio &&
        utilityType !== UtilityType.Electric
      ) {
        dataConnectorBillBreakdownTable &&
          dataConnectorBillBreakdownTable.actions &&
          dataConnectorBillBreakdownTable.actions.getAll();
      }

      setPrevRContextAdvnc(requestContext);
      // break;
      // }
    }
  }, [requestContext]);

  useEffect(() => {
    setIsOverview(apiContext.overViewElectricity);
    setIsMonthly(apiContext.isMonthlyElectricity);
    apiContext.basicAdvancedGrp
      ? setButtonGroup(apiContext.basicAdvancedGrp)
      : setButtonGroup([
          { content: t(d.BASIC), type: 'primary' },
          { content: t(d.ADVANCED), type: 'secondary' }
        ]);
    apiContext.monthlyBillingGrp
      ? setButtonGroupBilling(apiContext.monthlyBillingGrp)
      : setButtonGroupBilling([
          { content: t(d.MONTHLY), type: 'primary' },
          { content: t(d.BILLING), type: 'secondary' }
        ]);
  }, [
    apiContext.overViewElectricity,
    apiContext.isMonthlyElectricity,
    apiContext.basicAdvancedGrp,
    apiContext.monthlyBillingGrp
  ]);

  const performanceHeadingList = [
    t(d.ELECTRICITYPERFORMANCE),
    t(d.GASPERFORMANCEDETAILS),
    t(d.WaterPerformance),
    t(d.SEWERAGEPERFORMANCE)
  ];

  const [displayalert, setDisplayalert] = useState(true);
  const closealert = () => {
    setDisplayalert(false);
  };

  useEffect(() => {
    setDisplayalert(true);
  }, [isMonthly]);

  const commonProps = {
    performanceHeading: performanceHeadingList[activeTab]
  };

  // let dConnectorForOtherUtilityType;

  // switch (utilityType) {
  //   case UtilityType.Gas:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForGas;
  //     break;
  //   case UtilityType.Water:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForWater;
  //     break;
  //   case UtilityType.Sewerage:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForSewer;
  //     break;
  //   default:
  //     dConnectorForOtherUtilityType = dataConnectorBillBreakdownTableForGas;
  //     break;
  // }

  interface IBackControls {
    onBackarrowClick: () => void;
  }
  const BackControls = (props: IBackControls) => (
    <div className="emaas-utility-bill-viewDetails-kpi-card-title">
      <span className="utility-bill-backButton">
        <Icon
          root="common"
          name="arrow-left"
          size="medium"
          onClick={props.onBackarrowClick}
        />
      </span>
      {t(d.VIEW_OVERVIEW)}
    </div>
  );
  const isDetailedViewEnabled = [
    UtilityType.Electricity
    // UtilityType.Water
  ].includes(UtilityBillType || '');

  const checkOverview = isOverview || !isOverview;
  return isOutOfRange ? (
    <Card>
      <Card.Content>
        <div>{t(d.UTILITYYEAROUTOFRANGE)}</div>
      </Card.Content>
    </Card>
  ) : (
    <div className="Basic-Advance-Parent">
      <div style={displayalert ? {} : { height: '3rem' }}>
        {props.emaasContext?.toggleUtilityBreakdownVisualization &&
          !props.emaasContext?.forPortfolio &&
          isDetailedViewEnabled &&
          checkOverview && [
            <div className="billing-monthly-parent">
              <div className="ToggleTab">
                <Button.Group className="ToggleTabButton">
                  {buttonGroup.map(
                    (buttonData: IButtongroup, index: number) => (
                      <Button
                        className="ToggleTab-basic"
                        //type="primary"
                        key={index}
                        type={buttonData.type}
                        content={buttonData.content}
                        onClick={() => toggleButton(index, buttonData)}
                      />
                    )
                  )}
                </Button.Group>
              </div>
              {!isOverview && (
                <div className="ToggleTabBillingAdvanced">
                  <Button.Group className="ToggleTabButton">
                    {buttonGroupBilling.map(
                      (buttonData: IButtongroup, index: number) => (
                        <Button
                          className="ToggleTab-basic"
                          //type="primary"
                          key={index}
                          type={buttonData.type}
                          content={buttonData.content}
                          onClick={() =>
                            toggleButtonForMonthlyBilling(index, buttonData)
                          }
                        />
                      )
                    )}
                  </Button.Group>
                  <div className="Billing-Monthly-Tooltip">
                    {displayalert === false ? (
                      <Tooltip
                        element={
                          <Icon root="common" name="badge-info" size="small" />
                        }
                        event="hover"
                        header={''}
                        content={
                          isMonthly
                            ? t(d.NOTIFICATIONBOXFORMONTHLY)
                            : t(d.NOTIFICATIONFORBILLINGPERIOD)
                        }
                        position="top right"
                        size="small"
                      />
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              )}
            </div>,
            <div className="Electricity-Breakdown-Header">
              <div className="utility-bill-title">
                {!isOverview &&
                  t(d.UTILITY_BILL_ANALYSIS, {
                    utilityName: t(getUtilityTitle(props.utilityType))
                  })}
              </div>
            </div>,
            <div>
              {!isOverview && isMonthly == true
                ? displayalert && (
                    <Notification
                      hasIcon={true}
                      title="Monthly and Billing period view"
                      severity="information"
                      onCloseClick={() => {
                        closealert();
                      }}
                    >
                      {t(d.NOTIFICATIONBOXFORMONTHLY)}
                    </Notification>
                  )
                : !isOverview &&
                  displayalert && (
                    <Notification
                      hasIcon={true}
                      title="Billing period view"
                      severity="information"
                      onCloseClick={() => {
                        closealert();
                      }}
                    >
                      {t(d.NOTIFICATIONFORBILLINGPERIOD)}
                    </Notification>
                  )}
            </div>
          ]}
      </div>
      {/* {!isOverview && <BackControls onBackarrowClick={onBackarrowClick} />} */}
      {isOverview && (
        <>
          <UtilityBillPerformanceDetailComponent
            dataConnector={dataConnector}
            commonProps={commonProps}
            UtilityBillType={UtilityBillType} // once utilityType fixed from backend side then will remove this
            onViewDetailsClick={onViewDetailsClick}
            isOverview={isOverview}
          />
          <SavingsByMeterComponent
            dataConnector={dataConnector}
            UtilityBillType={UtilityBillType}
          />
          {!props.emaasContext?.forPortfolio &&
            utilityType !== UtilityType.Electric && (
              <UtilitySummaryWidget
                dataConnector={dataConnectorBillBreakdownTable}
                UtilityBillType={UtilityBillType}
                utilityType={utilityType}
                emaasContext={emaasContext}
              />
            )}
        </>
      )}
      {!isOverview && (
        <>
          <UtilityBillBreakdownChartWidget
            dataConnector={dataConnectorBillBreakdown}
            utilityType={utilityType}
            isMonthly={isMonthly}
          />
          <UtilitySummaryWidget
            dataConnector={dataConnectorBillBreakdown}
            UtilityBillType={UtilityBillType}
            utilityType={utilityType}
            isMonthly={isMonthly}
          />
        </>
      )}
    </div>
  );
};
export default UtilitySavingDetailsHOC;
